/**
 * Naming quality (TMMPDM-0112; tmm-api 0194 U3, YAML at `f7c843fb`): "Is naming getting better?" — a
 * read-only report on the AI model prices page. A period (URL `period`, Last 30 days by default) and three
 * sections, segmented within the tab (URL `by`): by naming source, by institution, and by model and
 * prompt version. Each row is the counts and the acceptance, edit and forget rates as the server sent
 * them, formatted as percentages; a rate the server didn't send (none when nothing was named) is "—",
 * never computed here. A catalog institution's id keys its row and is never shown.
 *
 * Until tmm-api deploys 0194 U3 the report answers a bare 404: "Naming quality isn't available yet."
 * with no retry. Counts only: no member, tenant or owner.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { adminApi } from '@/api/tmm/admin';
import { isActingAdminRevoked } from '@/api/errors';
import type { NamingQualityCounts, NamingQualityReport } from '@/api/tmm/types';
import { EmptyState, ErrorState } from '@/components/page';
import { formatNumber, formatRatioPercent } from '@/format';
import { periodFor, type PeriodKey } from '../dashboard/period';
import { isNotDeployed, notAvailable, sourceLabel } from '../knowledge/knowledge';

const PERIOD_KEYS: PeriodKey[] = ['LAST_30_DAYS', 'THIS_MONTH', 'LAST_MONTH'];
const SECTIONS = ['source', 'institution', 'model'] as const;
type Section = (typeof SECTIONS)[number];

type NamingView = { period: PeriodKey; by: Section };

const VIEW: UrlSchema<NamingView> = {
  period: { default: 'LAST_30_DAYS', parse: oneOf(PERIOD_KEYS) },
  by: { default: 'source', parse: oneOf(SECTIONS) },
};

const headerCellSx = { fontWeight: 600 } as const;

interface Row {
  key: string;
  label: string;
  secondary?: string;
  counts: NamingQualityCounts;
}

export function NamingQuality() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [view, setView] = useUrlState(VIEW);
  const period = useMemo(() => periodFor(view.period), [view.period]);
  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${period.from}:${period.to}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    result: NamingQualityReport | null;
    failure: unknown;
  }>({ requestKey: null, result: null, failure: null });

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getNamingQuality({ from: period.from, to: period.to })
      .then((result) => {
        if (!cancelled) setResponse({ requestKey, result, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setResponse({ requestKey, result: null, failure: failure ?? 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [period, requestKey]);

  const current = response.requestKey === requestKey;
  const failure = current ? response.failure : null;
  const result = current ? response.result : null;

  if (failure !== null && isNotDeployed(failure)) {
    return <EmptyState surface title={notAvailable(t, 'naming')} />;
  }

  const rows: Row[] =
    result === null
      ? []
      : view.by === 'source'
        ? result.bySource.map((row) => ({
            key: row.source,
            label: sourceLabel(row.source, t),
            counts: row,
          }))
        : view.by === 'institution'
          ? result.byInstitution.map((row, index) => ({
              // The catalog id keys the row; it is never shown. The unmatched row has none.
              key: row.institutionId === undefined ? `none:${index}` : String(row.institutionId),
              label:
                row.institutionName ??
                t('namingQuality.noInstitution', 'Not matched to a catalog bank'),
              counts: row,
            }))
          : result.byModel.map((row) => ({
              key: `${row.model}:${row.promptVersion}`,
              label: row.model,
              secondary: row.promptVersion,
              counts: row,
            }));

  const firstHeader =
    view.by === 'source'
      ? t('namingQuality.col.source', 'Naming source')
      : view.by === 'institution'
        ? t('namingQuality.col.institution', 'Institution')
        : t('namingQuality.col.model', 'Model');
  const sectionLabel = (section: Section) =>
    section === 'source'
      ? t('namingQuality.by.source', 'By naming source')
      : section === 'institution'
        ? t('namingQuality.by.institution', 'By institution')
        : t('namingQuality.by.model', 'By model and prompt version');

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'namingQuality.intro',
          'Is naming getting better? How often members accept, edit or forget the names they are given.',
        )}
      </Typography>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ mb: 2, alignItems: { xs: 'stretch', md: 'center' }, flexWrap: 'wrap' }}
      >
        <TextField
          select
          size="small"
          label={t('dashboard.period.label', 'Period')}
          value={view.period}
          onChange={(event) => {
            const next = oneOf(PERIOD_KEYS)(event.target.value);
            if (next) setView({ period: next });
          }}
          sx={{ minWidth: 200 }}
        >
          {PERIOD_KEYS.map((key) => (
            <MenuItem key={key} value={key}>
              {t(`dashboard.period.${key}`, key)}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view.by}
          aria-label={t('namingQuality.by.aria', 'Naming quality sections')}
          onChange={(_event, next: string | null) => {
            const section = next === null ? undefined : oneOf(SECTIONS)(next);
            if (section) setView({ by: section });
          }}
        >
          {SECTIONS.map((section) => (
            <ToggleButton key={section} value={section}>
              {sectionLabel(section)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {failure ? (
        <ErrorState
          error={failure}
          message={t('namingQuality.loadError', 'Could not load the naming quality report.')}
          {...(isActingAdminRevoked(failure)
            ? {}
            : { onRetry: () => setReloadToken((n) => n + 1) })}
        />
      ) : result === null ? (
        <Typography variant="body2" color="text.secondary">
          {t('common.loading', 'Loading')}
        </Typography>
      ) : rows.length === 0 ? (
        <EmptyState surface title={t('namingQuality.empty', 'No names in this period.')} />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={sectionLabel(view.by)}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>{firstHeader}</TableCell>
                {view.by === 'model' ? (
                  <TableCell sx={headerCellSx}>
                    {t('namingQuality.col.promptVersion', 'Prompt version')}
                  </TableCell>
                ) : null}
                <TableCell sx={headerCellSx} align="right">
                  {t('namingQuality.col.named', 'Names')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('namingQuality.col.accepted', 'Accepted')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('namingQuality.col.edited', 'Edited')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('namingQuality.col.forgotten', 'Forgotten')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('namingQuality.col.acceptanceRate', 'Acceptance rate')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('namingQuality.col.editRate', 'Edit rate')}
                </TableCell>
                <TableCell sx={headerCellSx} align="right">
                  {t('namingQuality.col.forgetRate', 'Forget rate')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{row.label}</TableCell>
                  {view.by === 'model' ? (
                    <TableCell sx={(theme) => ({ ...theme.typography.mono })}>
                      {row.secondary}
                    </TableCell>
                  ) : null}
                  <TableCell align="right">{formatNumber(row.counts.named, locale)}</TableCell>
                  <TableCell align="right">{formatNumber(row.counts.accepted, locale)}</TableCell>
                  <TableCell align="right">{formatNumber(row.counts.edited, locale)}</TableCell>
                  <TableCell align="right">{formatNumber(row.counts.forgotten, locale)}</TableCell>
                  <TableCell align="right">
                    {formatRatioPercent(row.counts.acceptanceRate, locale)}
                  </TableCell>
                  <TableCell align="right">
                    {formatRatioPercent(row.counts.editRate, locale)}
                  </TableCell>
                  <TableCell align="right">
                    {formatRatioPercent(row.counts.forgetRate, locale)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
